import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { isActiveTier, isUserInsolvencyTier, UserInsolvencyTierDto } from '../../../config/finance/financeConfig'
import { useSettings } from '../../../hooks/useSettings'
import FormattableNumberInput, { CommitEventType, FormattableNumberInputProps } from '../../Input/FormattableNumberInput'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../Combobox/EnumMapCombobox'
import UserCombobox from '../../Combobox/UserCombobox'
import * as Yup from 'yup'
import { SubComboboxProps } from '../../Combobox/BaseCombobox'
import { ActiveUserStatuses, UserListItemDto } from '../../../config/userConfig'
import FormikField from '../../Formik/FormikField'
import { useFormikContext } from 'formik'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import useUsers from '../../../hooks/useUsers'
import { usePrevious } from '@fluentui/react-hooks'
import { TierDialogProps } from './TierDialog'
import { FileMutationDto } from '../../../config/fileConfig'
import { TierAction } from './TierList'
import { parseNumber } from '../../../helpers/numberHelper'

export const userInsolvencyTierValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        userId: Yup.string().label(translate('file.edit.form.finance.rates.tier.userid')).nonNullable().required(),
        role: Yup.string().label(translate('file.edit.form.finance.rates.tier.role')).nonNullable().required(),
        purchaseRate: Yup.number().label(translate('file.edit.form.finance.rates.tier.purchaserate')).nonNullable().required(),
        salesRate: Yup.number().label(translate('file.edit.form.finance.rates.tier.salesrate')).nonNullable().required(),
    })
}

export default function UserInsolvencyTierFormContainer(
    props: TierDialogProps<FileMutationDto<UserInsolvencyTierDto>, UserInsolvencyTierDto>,
): ReactElement {
    const { values, setFieldValue } = useFormikContext<UserInsolvencyTierDto>()
    const settings = useSettings()
    const { users } = useUsers()
    const prevTierValues = usePrevious(values)

    const insolvencyRoles = useMemo<Map<string, string>>(
        () => new Map(settings?.insolvency?.roles.map(x => [x.key, x.name] as [string, string])),
        [settings?.insolvency],
    )

    useEffect(() => {
        const userTier = users
            ?.find(user => user.id === values.userId)
            ?.tiers?.filter(tier => isUserInsolvencyTier(tier))
            ?.filter(tier => isActiveTier(tier))
            ?.find(tier => tier.currency === values.currency)

        if (
            (userTier && prevTierValues && values.userId !== prevTierValues.userId) ||
            (prevTierValues === undefined && props.action === TierAction.ADD)
        ) {
            setFieldValue(`role`, userTier?.role ?? values.role)
            setFieldValue(`estateRate`, userTier?.estateRate ?? values.estateRate)
            setFieldValue(`factorExperience`, userTier?.factorExperience ?? values.factorExperience)
            setFieldValue(`factorEstate`, userTier?.factorEstate ?? values.factorEstate)
            setFieldValue(`factorTotal`, userTier?.factorTotal ?? values.factorTotal)
        }
    }, [values.userId])

    useEffect(() => {
        if (values.factorTotal && values.estateRate) {
            const value = parseNumber(values.factorTotal, 'en') * parseNumber(values.estateRate, 'en')
            setFieldValue(`salesRate`, value.toString())
        }
    }, [values.factorTotal, values.estateRate])

    useEffect(() => {
        if (values.factorExperience && values.factorEstate) {
            const value = Math.ceil(parseNumber(values.factorExperience, 'en') * parseNumber(values.factorEstate, 'en') * 10) / 10
            setFieldValue(`factorTotal`, value.toString())
        }
    }, [values.factorExperience, values.factorEstate])

    return (
        <>
            <FormikField<SubComboboxProps<UserListItemDto>, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.userid'}
                fieldName={'userId'}
                component={UserCombobox}
                componentProps={{
                    multiselect: false,
                    selectedOptions: [values.userId],
                    onOptionSelect: (_, data) => setFieldValue('userId', data.selectedOptions?.[0]),
                    filter: entity =>
                        (!props.parentValues.tiers?.find(tier => tier.userId === entity.id) && ActiveUserStatuses.includes(entity.status)) ||
                        entity.id === values.userId,
                }}
            />
            <FormikField<EnumMapComboboxProps<string>, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.role'}
                fieldName={'role'}
                component={EnumMapCombobox<string>}
                componentProps={{
                    values: insolvencyRoles,
                    multiselect: false,
                    selectedOptions: [values.role],
                    onOptionSelect: (_, data) => setFieldValue('role', data.selectedOptions?.[0]),
                }}
            />
            <FormikField<FormattableNumberInputProps, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.factorexperience'}
                fieldName={'factorExperience'}
                component={FormattableNumberInput}
                componentProps={{
                    allowNegative: false,
                    decimals: 1,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('factorExperience', value)
                        }
                    },
                }}
            />
            <FormikField<FormattableNumberInputProps, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.factorestate'}
                fieldName={'factorEstate'}
                component={FormattableNumberInput}
                componentProps={{
                    allowNegative: false,
                    decimals: 1,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('factorEstate', value)
                        }
                    },
                }}
            />

            <FormikField<FormattableNumberInputProps, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.factortotal'}
                fieldName={'factorTotal'}
                component={FormattableNumberInput}
                componentProps={{
                    allowNegative: false,
                    decimals: 1,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('factorTotal', value)
                        }
                    },
                }}
            />
            <FormikField<MoneyInputProps, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.estaterate'}
                fieldName={'estateRate'}
                component={MoneyInput}
                componentProps={{
                    currency: values?.currency,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('estateRate', value)
                        }
                    },
                }}
            />
            <FormikField<MoneyInputProps, UserInsolvencyTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.salesrate'}
                fieldName={'salesRate'}
                component={MoneyInput}
                componentProps={{
                    currency: values?.currency,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('salesRate', value)
                        }
                    },
                }}
            />
        </>
    )
}
